import { create } from "@bufbuild/protobuf";
import { FieldMaskSchema } from "@bufbuild/protobuf/wkt";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { journalServiceClient } from "@/connect";
import { useTickerContext } from "@/contexts/TickerContext";
import { tickerKeys } from "@/hooks/useTickerQueries";
import { userKeys } from "@/hooks/useUserQueries";
import type { ListMemosRequest, Memo } from "@/types/proto/api/v1/memo_service_pb";
import { ListMemosRequestSchema, MemoSchema } from "@/types/proto/api/v1/memo_service_pb";
import { activityStatsKeys } from "./useActivityStats";

// Query keys factory for consistent cache management
export const memoKeys = {
  all: ["memos"] as const,
  lists: () => [...memoKeys.all, "list"] as const,
  list: (filters: Partial<ListMemosRequest>) => [...memoKeys.lists(), filters] as const,
  details: () => [...memoKeys.all, "detail"] as const,
  detail: (name: string, includeAi?: boolean) => [...memoKeys.details(), name, includeAi ?? false] as const,
  versions: (name: string) => [...memoKeys.all, "versions", name] as const,
  version: (name: string, version: number) => [...memoKeys.versions(name), version] as const,
  versionDiff: (name: string, v1: number, v2: number) => [...memoKeys.all, "diff", name, v1, v2] as const,
};

export function useMemos(request: Partial<ListMemosRequest> = {}) {
  return useQuery({
    queryKey: memoKeys.list(request),
    queryFn: async () => {
      const response = await journalServiceClient.listMemos(create(ListMemosRequestSchema, request as Record<string, unknown>));
      return response;
    },
  });
}

/**
 * Batch fetch memos by their UIDs in a single API call.
 * This is more efficient than calling getMemo multiple times.
 * @param uids - Array of memo UIDs to fetch
 * @returns Map of UID -> Memo (not found UIDs are omitted)
 */
export function useBatchGetMemos(uids: string[]) {
  return useQuery({
    queryKey: [...memoKeys.all, "batch", uids],
    queryFn: async () => {
      if (uids.length === 0) {
        return {};
      }
      const response = await journalServiceClient.batchGetMemosByUIDs({ uids });
      return response.memos;
    },
    enabled: uids.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useInfiniteMemos(request: Partial<ListMemosRequest> = {}) {
  const queryKey = memoKeys.list(request);

  return useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const response = await journalServiceClient.listMemos(
        create(ListMemosRequestSchema, {
          ...request,
          pageToken: pageParam || "",
        } as Record<string, unknown>),
      );
      return response;
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    staleTime: 1000 * 60, // Consider data fresh for 1 minute
    gcTime: 1000 * 60 * 5, // Keep unused data in cache for 5 minutes
  });
}

export function useMemo(name: string, options?: { enabled?: boolean; includeAiAnalysis?: boolean }) {
  const includeAi = options?.includeAiAnalysis ?? false;
  return useQuery({
    queryKey: memoKeys.detail(name, includeAi),
    queryFn: async () => {
      const memo = await journalServiceClient.getMemo({
        name,
        includeAiAnalysis: includeAi,
      });
      return memo;
    },
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60, // 1 minute - memos can be edited frequently
  });
}

export function useUpdateMemo() {
  const queryClient = useQueryClient();
  const { symbol: currentTickerSymbol } = useTickerContext();

  return useMutation({
    mutationFn: async ({ update, updateMask }: { update: Partial<Memo>; updateMask: string[] }) => {
      const memo = await journalServiceClient.updateMemo({
        memo: create(MemoSchema, update as Record<string, unknown>),
        updateMask: create(FieldMaskSchema, { paths: updateMask }),
      });
      return memo;
    },
    onMutate: async ({ update }) => {
      if (!update.name) {
        return { previousMemo: undefined };
      }

      // Cancel outgoing refetches to prevent race conditions
      // Invalidate both with and without AI analysis to ensure cache consistency
      await queryClient.cancelQueries({ queryKey: memoKeys.details() });

      // Snapshot previous value for rollback on error
      const previousMemo = queryClient.getQueryData<Memo>(memoKeys.detail(update.name, false));
      const previousMemoWithAi = queryClient.getQueryData<Memo>(memoKeys.detail(update.name, true));

      // Optimistically update the cache
      if (previousMemo) {
        queryClient.setQueryData(memoKeys.detail(update.name, false), { ...previousMemo, ...update });
        queryClient.setQueryData(memoKeys.detail(update.name, true), { ...previousMemoWithAi, ...update });
      }

      return { previousMemo, previousMemoWithAi };
    },
    onError: (_err, { update }, context) => {
      // Rollback on error
      if (update.name) {
        if (context?.previousMemo) {
          queryClient.setQueryData(memoKeys.detail(update.name, false), context.previousMemo);
        }
        if (context?.previousMemoWithAi) {
          queryClient.setQueryData(memoKeys.detail(update.name, true), context.previousMemoWithAi);
        }
      }
    },
    onSuccess: async (updatedMemo) => {
      // Update cache with server response
      queryClient.setQueryData(memoKeys.detail(updatedMemo.name, false), updatedMemo);
      // Invalidate AI version to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: [memoKeys.all, "detail", updatedMemo.name, true],
      });
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: memoKeys.lists() });
      // Invalidate user stats
      queryClient.invalidateQueries({ queryKey: userKeys.stats() });
      // Invalidate activity stats for calendar heatmap
      queryClient.invalidateQueries({ queryKey: activityStatsKeys.all });

      // If we're viewing a TickerDetail page, invalidate that ticker's detail query
      // to refresh the Associated Journals list (e.g., after archive/pin operations)
      if (currentTickerSymbol) {
        await queryClient.invalidateQueries({ queryKey: tickerKeys.detail(currentTickerSymbol) });
      }
    },
  });
}

export function useDeleteMemo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      await journalServiceClient.deleteMemo({ name });
      return name;
    },
    onSuccess: (name) => {
      // Remove from cache (both with and without AI analysis)
      queryClient.removeQueries({ queryKey: memoKeys.detail(name, false) });
      queryClient.removeQueries({ queryKey: memoKeys.detail(name, true) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: memoKeys.lists() });
      // Invalidate user stats
      queryClient.invalidateQueries({ queryKey: userKeys.stats() });
      // Invalidate activity stats for calendar heatmap
      queryClient.invalidateQueries({ queryKey: activityStatsKeys.all });
    },
  });
}

/**
 * Get a specific version of a memo
 * @param name - Memo resource name (e.g., "memos/123")
 * @param version - Version number
 * @param options - React Query options
 */
export function useMemoVersion(name: string, version: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: memoKeys.version(name, version),
    queryFn: async () => {
      const response = await journalServiceClient.getMemoVersion({ name, version });
      return response;
    },
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 10, // 10 minutes - versions don't change
  });
}

/**
 * List all versions of a memo
 * @param name - Memo resource name (e.g., "memos/123")
 * @param options - React Query options
 */
export function useMemoVersions(name: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: memoKeys.versions(name),
    queryFn: async () => {
      const response = await journalServiceClient.listMemoVersions({ name });
      return response;
    },
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get diff between two versions of a memo
 * @param name - Memo resource name (e.g., "memos/123")
 * @param version1 - First version number
 * @param version2 - Second version number
 * @param format - Diff format ("text" or "json")
 * @param options - React Query options
 */
export function useMemoVersionDiff(
  name: string,
  version1: number,
  version2: number,
  format: string = "text",
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: memoKeys.versionDiff(name, version1, version2),
    queryFn: async () => {
      const response = await journalServiceClient.diffMemoVersions({ name, version1, version2, format });
      return response;
    },
    enabled: (options?.enabled ?? true) && version1 > 0 && version2 > 0,
    staleTime: 1000 * 60 * 10, // 10 minutes - versions don't change
  });
}
